import React, { useContext, useState } from 'react';
import useDataSource from '@afosto/app-components-react/DataSource/useDataSource';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePacking from '../../../utils/usePacking';
import usePickQuantities from './usePickQuantities';
import useScanner from '@afosto/utils-react/input/useScanner';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import ProcessHandlingListContext from '../../../components/ProcessHandlingListContext';
import CheckParcelsDialog from '../../../components/CheckParcelsDialog';
import PickAllDialog from './PickAllDialog';
import PickContainer from './PickContainer';
import PickFinishedDialog from './PickFinishedDialog';
import PickFollowUpActionsDialog from './PickFollowUpActionsDialog';
import PickItem from './PickItem';
import PickItemSkeleton from './PickItem/PickItemSkeleton';
import PickMissingItemsDialog from './PickMissingItemsDialog';
import PickProvider from './PickProvider';
import WrongBarcodeDialog from '../../../components/WrongBarcodeDialog';
import PaginatedView from '@afosto/app-components-react/PaginatedView';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import { bulk as bulkApi } from '@afosto/fulfilment/api/Bulk';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import translations from './translations';

const DEFAULT_ITEM_STATUS_CONSTRAINTS = [
  {
    key: 'is_missing',
    operator: 'in',
    value: false,
  },
];

const Pick = () => {
  const { handlingList, shouldPack, showPickAllDialog, setShowPickAllDialog } = useContext(
    ProcessHandlingListContext,
  );
  const handlingListId = handlingList?.id;
  const confirm = useConfirm();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const shipments = handlingList?.shipments ?? [];
  const [firstShipmentId] = shipments;
  const packingContext = usePacking(firstShipmentId, shouldPack);
  const {
    currentParcel,
    handleCheckParcels,
    handleCreateParcel,
    handleDeleteParcel,
    handlePackingDocuments,
    isCreatingParcel,
    isDeletingParcel,
    parcels,
    showCheckParcelsDialog,
    setShowCheckParcelsDialog,
  } = packingContext;
  const [showFinishedPickDialog, setShowFinishedPickDialog] = useState(false);
  const [showFollowUpActionsDialog, setShowFollowUpActionsDialog] = useState(false);
  const [showMissingItemsDialog, setShowMissingItemsDialog] = useState(false);
  const [showWrongBarcodeDialog, setShowWrongBarcodeDialog] = useState(false);

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.searchHandlingListItems(
          {
            constraints: [
              {
                key: 'list_ids',
                operator: 'in',
                value: [handlingListId],
              },
              ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
            ],
          },
          requestOptions,
        ),
      {
        transformData: async data => {
          const [firstItem] = data ?? [];
          const skus = data.map(({ sku }) => sku);
          const products = await fetchMultipleProducts(skus);
          const mappedProducts = products.reduce(
            (acc, product) => ({ ...acc, [product?.sku]: product }),
            {},
          );
          const response = await searchApi.listListItems({
            constraints: [
              {
                key: 'ids',
                operator: 'in',
                value: firstItem.ids ?? [],
              },
              {
                key: 'shipment_ids',
                operator: 'in',
                value: [firstItem.shipment_id],
              },
            ],
          });
          const [shipmentItem] = response?.data ?? [];
          const itemParcels = (shipmentItem?.parcels ?? []).reduce(
            (acc, parcel) => ({ ...acc, [parcel.id]: parcel }),
            {},
          );
          const boxedIds = (shipmentItem?.parcels ?? []).reduce(
            (acc, parcel) => [...acc, ...(parcel?.item_ids ?? [])],
            [],
          );

          return data.reduce((acc, item) => {
            const [firstId] = item?.ids ?? [];
            return [
              ...acc,
              {
                ...item,
                id: firstId,
                product: mappedProducts[item.sku],
                parcels: itemParcels,
                boxed_ids: boxedIds,
              },
            ];
          }, []);
        },
      },
    );

  const dataSource = useDataSource(fetchItems, {
    modules: ['pagination'],
    pagination: {
      itemsPerPage: 1,
    },
  });
  const [currentItem] = dataSource?.data ?? [];

  const handleSaveItem = async (item, itemQuantities, position) => {
    try {
      const { quantity } = itemQuantities;
      const difference = quantity - (item?.handled_ids.length ?? 0);
      const isHandled = difference > 0;
      const itemIds = item?.ids ?? [];
      const itemParcel = shouldPack ? item?.parcels?.[currentParcel.id] : null;
      const ids = itemIds
        .filter(itemId => {
          if (difference > 0) {
            const itemIsNotHandled = !item?.handled_ids?.includes(itemId);

            if (shouldPack) {
              return itemIsNotHandled && !item?.boxed_ids?.includes(itemId);
            }

            return itemIsNotHandled;
          }

          if (difference < 0) {
            const itemIsHandled = !!item?.handled_ids?.includes(itemId);

            if (shouldPack && itemParcel) {
              return itemIsHandled && !!itemParcel?.item_ids?.includes(itemId);
            }

            return itemIsHandled;
          }

          return false;
        })
        .slice(0, Math.abs(difference));

      if (ids.length > 0) {
        if (shouldPack && !isHandled) {
          await parcelsApi.removeFromParcel(currentParcel.id, {
            ids,
          });
        }

        await bulkApi.bulkUpdateItems({
          command: 'set_parameters',
          parameters: {
            is_handled: isHandled,
            ...(position ? { position } : {}),
          },
          constraints: [
            {
              key: 'list_ids',
              operator: 'in',
              value: [handlingListId],
            },
            {
              key: 'ids',
              operator: 'in',
              value: ids,
            },
            ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
          ],
        });

        if (shouldPack && isHandled) {
          await parcelsApi.addToParcel(currentParcel.id, {
            ids,
          });
        }
      }

      return { ids, isHandled };
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.itemNotProcessed), {
        variant: 'error',
      });

      return Promise.reject(err);
    }
  };

  const handleFinishPicking = async (checkParcels = true) => {
    try {
      if (shouldPack) {
        if (checkParcels) {
          await handleCheckParcels();
          return;
        }

        await handlePackingDocuments();
        setShowFinishedPickDialog(true);
      } else {
        setShowFollowUpActionsDialog(true);
      }
    } catch (err) {
      // Do nothing.
    }
  };

  const handleCheckMissingItems = async () => {
    try {
      const response = await searchApi.searchHandlingListItems({
        constraints: [
          {
            key: 'list_ids',
            operator: 'in',
            value: [handlingListId],
          },
          {
            key: 'is_handled',
            operator: 'in',
            value: false,
          },
          ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
        ],
      });

      if (response?.data?.length > 0) {
        setShowMissingItemsDialog(true);
        return;
      }

      handleFinishPicking();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.missingItemsRequestFailed), {
        variant: 'error',
      });
    }
  };

  const handleFinishPickAll = async () => {
    await handleCheckMissingItems();
    setShowPickAllDialog(false);
  };

  const handleAutomaticNextItem = async (item, itemQuantities) => {
    await handleSaveItem(item, itemQuantities);

    const { activePage, itemCount = 0, nextPage } = dataSource?.pagination ?? {};
    const isLastPage = activePage === itemCount - 1;

    if (isLastPage) {
      await handleCheckMissingItems();
      return;
    }

    nextPage();
  };

  const { itemKey, itemQuantityCounter } = usePickQuantities(
    currentItem,
    packingContext,
    handleAutomaticNextItem,
  );
  const quantities = itemQuantityCounter?.itemQuantities[itemKey] ?? {};

  const lessThanRequested = () => {
    const { requested = 0, quantity = 0 } = quantities;
    return quantity < requested;
  };

  const handleScanInput = inputValue => {
    const { handlePlus } = itemQuantityCounter ?? {};

    if (inputValue && inputValue === currentItem?.product?.ean) {
      handlePlus(itemKey)();
    } else if (inputValue) {
      setShowWrongBarcodeDialog(true);
    }
  };

  useScanner(handleScanInput);

  const handleSaveCurrentItem = async (reloadItem = false) => {
    await handleSaveItem(currentItem, quantities);

    if (reloadItem) {
      dataSource.reloadDataSource();
    }
  };

  const handleConfirmLessPicked = async () =>
    confirm({
      title: intl.formatMessage(translations.lessPickedDialog.title),
      description: intl.formatMessage(translations.lessPickedDialog.description),
      catchOnCancel: true,
      disableBackdropClick: true,
      buttons: [
        {
          label: intl.formatMessage(translations.lessPickedDialog.buttons.check),
          type: 'cancel',
          variant: 'outlined',
          style: { marginRight: 'auto' },
        },
        {
          label: intl.formatMessage(translations.lessPickedDialog.buttons.continue),
          response: 'continue',
          type: 'submit',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

  const handlePreviousItem = () => {
    handleSaveCurrentItem().catch(() => {});
  };

  const handleNextItem = async () => {
    try {
      const lessPicked = lessThanRequested();

      if (lessPicked) {
        await handleConfirmLessPicked();
      }

      handleSaveCurrentItem().catch(() => {});

      return true;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleFinishItem = async () => {
    try {
      const lessPicked = lessThanRequested();

      if (lessPicked) {
        await handleConfirmLessPicked();
      }

      await handleSaveCurrentItem();
      await handleCheckMissingItems();

      return true;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleCloseCheckParcels = () => {
    setShowCheckParcelsDialog(false);
    handleFinishPicking(false).catch(() => {});
  };

  const renderItem = item => (
    <PickItem
      key={item.id}
      item={item}
      itemKey={itemKey}
      itemQuantityCounter={itemQuantityCounter}
      showPacking={shouldPack}
    />
  );

  const renderItemSkeleton = key => (
    <PickItemSkeleton key={key} showCrate={shipments?.length > 1} showPacking={shouldPack} />
  );

  return (
    <PickProvider value={{ ...packingContext, handleSaveCurrentItem, shouldPack }}>
      <PickContainer>
        <PaginatedView
          dataSource={dataSource}
          backLabel={intl.formatMessage(translations.actions.back)}
          finishLabel={intl.formatMessage(translations.actions.finish)}
          nextLabel={intl.formatMessage(translations.actions.next)}
          onBack={handlePreviousItem}
          onFinish={handleFinishItem}
          onNext={handleNextItem}
          render={renderItem}
          renderSkeleton={renderItemSkeleton}
        />
      </PickContainer>
      <CheckParcelsDialog
        isLoading={isCreatingParcel || isDeletingParcel}
        isOpen={showCheckParcelsDialog}
        onContinue={handleCloseCheckParcels}
        onCreate={handleCreateParcel}
        onDelete={handleDeleteParcel}
        parcels={parcels}
      />
      <PickAllDialog
        currentParcel={currentParcel}
        isOpen={showPickAllDialog}
        onCancel={() => setShowPickAllDialog(false)}
        onSuccess={handleFinishPickAll}
      />
      <PickMissingItemsDialog
        isOpen={showMissingItemsDialog}
        onFinish={handleFinishPicking}
        onSaveItem={handleSaveItem}
      />
      <PickFinishedDialog isOpen={showFinishedPickDialog} />
      <PickFollowUpActionsDialog isOpen={showFollowUpActionsDialog} />
      <WrongBarcodeDialog
        isOpen={showWrongBarcodeDialog}
        onClose={() => setShowWrongBarcodeDialog(false)}
      />
    </PickProvider>
  );
};

export default Pick;
